import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { StarRatingEvents } from '../types';
import { isEmpty } from '@nestjs/common/utils/shared.utils';
import { HTMLInputElement } from 'happy-dom';

/**
 * @summary star rating component
 * @tag gz-star-rating
 */

@customElement('gz-star-rating')
export class GzStarRatingWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  value: string;

  @property({ type: Boolean, reflect: true, attribute: true })
  disabled = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  readonly = false;

  protected invalid = false;

  static get styles() {
    return styles;
  }

  private starValues: number[] = [5,4,3,2,1];

  render(): TemplateResult {
    return html`
      <div class="star-rating d-flex flex-row-reverse justify-start" @change="${this.handleChange}">
       ${this.starValues.map(value => this.renderStartItem(value))}
      </div>
    `;
  }

  private renderStartItem(item:number): TemplateResult {
    return html`
      <input gz-form-input type="radio" id="${item}-stars" name="rating" value="${item}" />
      <label for="${item}-stars" class="star">
        <gz-icon class="star" name="ds-icon-favorites_star_filled" size="24"></gz-icon>
      </label>`
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('value')) {
      this.updateComplete.then(() => this.selectedCurrentValue());
    }
  }

  private selectedCurrentValue(): void {
    const element: HTMLInputElement | null = this.shadowRoot?.querySelector(
      `input[value='${this.value}']`
    ) as HTMLInputElement | null;

    if (!element) {
      return;
    }
    element.checked = true;
  }

  private handleChange(event: Event): void {
    const value = event.target?.['value'];

    if (isEmpty(value)) {
      return;
    }

    this.value = value;
    this.dispatchEvent(
      new CustomEvent(StarRatingEvents.CHANGE, {
        detail: {
          selectedValue: value,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  // public functions needed for gzFormField
  public getFormValue(): string {
    return this.value;
  }

  public setEnabled(isEnabled: boolean): void {
    this.disabled = !isEnabled;
  }

  public setReadonly(isReadonly: boolean): void {
    this.readonly = isReadonly;
  }

  public markAsInvalid(): void {
    if (this.invalid) {
      return;
    }

    this.invalid = true;
    this.requestUpdate('invalid', false);
  }

  public markAsValid(): void {
    if (!this.invalid) {
      return;
    }

    this.invalid = false;
    this.requestUpdate('invalid', true);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-star-rating': GzStarRatingWebComponent;
  }
}
